import React from "react";
import { connect } from "react-redux";
import { compose } from "redux";
import moment from "moment";
import { cloneDeep, sortBy } from "lodash";
import { withRouter } from "react-router";
import { CardPageLayout } from "component-lib/CardPageLayout";
import { ToastError } from "../../components/Global/Toasts/Toasty";
import { mapCompanySelector } from "store/state/auth";
import {
  updateProperty,
  propertyDetailGetProperty,
  propertyDetailGetDealMetrics,
  propertyDetailGetNewProperty,
  propertyDetailCreateProperty,
  getContactsByCompany,
  getCompaniesByTypeId,
  getSalesBrokers,
  getPropertiesAndProjectionsOfDealByPropertyId,
  downloadProjections,
  extractProjections
} from "./propertyDetail.store";
import { DEAL_METRICS } from "@loanbossinc/component-library";
import { PROPERTY_SECTION_KEYS } from "./property.constants";
import { PropertyHeroCard } from "component-lib/PropertyHeroCard";
import { AccordionEditSection } from "component-lib/AccordionSection";
import { rows as financialRows } from "./property-general-information/section-property-financials-edit-rows";
import { rows as deatailRows } from "./property-general-information/section-property-details-edit-rows";
import { rows as locationRows } from "./property-general-information/section-property-location-edit-rows";
import { rows as historyRows } from "./property-history/section-property-history-edit-rows";
import { rows as newPropertyRows } from "./property-new/section-property-new-edit-rows";
import { rows as individualOwnerRows } from "./property-ownership/section-property-individual-owner-edit-rows";
import { rows as companyOwnerRows } from "./property-ownership/section-property-company-owner-edit-rows";
import { rows as dispositionRows } from "./property-disposition/section-property-disposition-edit-rows";
import { rows as projectionsRows } from "./projections-variables/projections-variables-edit.rows";
import { createProperty, deleteProperty } from "store/state/property";
import { getConfigItemsByParents } from "store/state/configItems";
import LenderReportingDrawer from "../lenderReporting/drawer";
import { getAccountingIntegrationsForModel, createAccountingIntegration, deleteAccountingIntegration } from "../../store/state/accountingIntegration";
import { dealGet } from "../deal/deal.store";
import { SimpleInfoModal } from "../../modals/SimpleInfoModal";

const dealMetrics = [DEAL_METRICS.CLOSING_DATE, DEAL_METRICS.PURCHASE_PRICE, DEAL_METRICS.SQUARE_FEET];

const Quarters = {
  1: 1,
  2: 1,
  3: 1,
  4: 2,
  5: 2,
  6: 2,
  7: 3,
  8: 3,
  9: 3,
  10: 4,
  11: 4,
  12: 4
};

class PropertySectionEdit extends React.Component {
  state = {
    isSaving: false,
    isCanceling: false,
    isLenderReportingDrawerOpen: false,
    fieldNameForLenderReporting: undefined
  };

  componentDidMount() {
    document.getElementById("ScrollToNav").scrollIntoView({ behavior: "smooth" });

    const { propertyId, sectionName, dealId } = this.props.match.params;

    // NOTE: Handles the scenario that the store is not loaded when you come to this page.
    //       ex: refresh page.

    const { propertyData } = this.props;
    const { property } = propertyData;

    if (sectionName === PROPERTY_SECTION_KEYS.NEW_PROPERTY) {
      this.props.propertyDetailGetNewProperty({ dealId }); // this needs to stub out a new property
    } else if (!property) {
      this.props.propertyDetailGetProperty({ propertyId });
      this.props.propertyDetailGetDealMetrics({ propertyId, dealMetrics });
      this.props.getPropertiesAndProjectionsOfDealByPropertyId({ propertyId });
      this.props.getAccountingIntegrationsForModel("property", propertyId);
    }
  }

  componentDidUpdate(prevProps, prevState) {
    const currentPropertyId = this.props?.match?.propertyId;
    const prevPropertyId = prevProps?.match?.propertyId;

    if (currentPropertyId && prevPropertyId && currentPropertyId !== prevPropertyId) {
      this.props.propertyDetailGetProperty({ currentPropertyId });
      this.props.getPropertiesAndProjectionsOfDealByPropertyId({ currentPropertyId });
    }
  }

  getFinancialsSubTitle = () => {
    const { propertyData } = this.props;
    const date = propertyData.property.noiAsOfDate;

    if (!date) return null;
    return `As Of ${moment(date).format("MM/DD/YYYY")}`;
  };

  deleteFalsyValues = property => {
    this.deleteIfFalsy(property, "squareFeet");
    this.deleteIfFalsy(property, "units");
    this.deleteIfFalsy(property, "yearBuilt");
    this.deleteIfFalsy(property, "yearRenovated");
    this.deleteIfFalsy(property, "salesPrice");
    this.deleteIfFalsy(property, "noi");
    this.deleteIfFalsy(property, "noiActual");
    this.deleteIfFalsy(property, "appraisedValue");
    this.deleteIfFalsy(property, "purchasePrice");
    this.deleteIfFalsy(property, "landSize");
    this.deleteIfFalsy(property, "occupancy");
    this.deleteIfFalsy(property, "capExImprovements");
    this.deleteIfFalsy(property, "projectedExitDate");
    this.deleteIfFalsy(property, "actualExitDate");
    this.deleteIfFalsy(property, "purchaseDate");
    this.deleteIfFalsy(property, "noiAsOfDate");
    this.deleteIfFalsy(property, "marketValue");
    this.deleteIfFalsy(property, "marketValueAsOfDate");
    this.deleteIfFalsy(property, "brokerOpinionValue");
    this.deleteIfFalsy(property, "brokerOpinionValueAsOfDate");
    this.deleteIfFalsy(property, "noiAsOfappraisedValueAsOfDateDate");
    this.deleteIfFalsy(property, "hotelRooms");
  };

  deleteIfFalsy(property, fieldName) {
    if (!property[fieldName] && property[fieldName] !== 0) property[fieldName] = null;
  }

  redirectToDetails = () => {
    const { history, newPropertyId } = this.props;
    if (newPropertyId) {
      history.replace(`/properties/${newPropertyId}`);
      this.props.propertyDetailGetProperty({ newPropertyId });
      this.props.propertyDetailGetDealMetrics({ newPropertyId, dealMetrics });
      this.props.getPropertiesAndProjectionsOfDealByPropertyId({ newPropertyId });
    }
  };

  validateProperty = property => {
    let totalOwnershipPercentage = 0;

    if (property.companyOwners) {
      totalOwnershipPercentage += property.companyOwners.reduce((accum, owner) => {
        if (!owner) return accum;
        accum += (owner.ownershipPercentage * 1) / 100;
        return accum;
      }, 0);
    }

    if (property.individualOwners) {
      totalOwnershipPercentage += property.individualOwners.reduce((accum, owner) => {
        if (!owner) return accum;
        accum += (owner.ownershipPercentage * 1) / 100;
        return accum;
      }, 0);
    }

    if (totalOwnershipPercentage > 1) {
      ToastError("Ownership is greater than 100% across all owners");
      return false;
    }

    if (property.salesBrokerCompanyId && !property.salesBrokerContactId) {
      ToastError("Please select a Sales Broker Contact");
      return false;
    }

    return true;
  };

  makeDecimal = (property, field) => {
    property[field] = property[field] ? property[field] / 100 : null;
  };

  modifyValuesForSerialization = property => {
    this.makeDecimal(property, "occupancy");
    this.makeDecimal(property, "capRateAssumption");
    this.makeDecimal(property, "noiActualBumpAssumption");
    this.makeDecimal(property, "occupancyAssumption");
    this.makeDecimal(property, "exitCapRate");
    this.makeDecimal(property, "exitDebtYield");
    this.makeDecimal(property, "cashOnCash");
    this.makeDecimal(property, "internalRateOfReturn");
    if (!!property.companyOwners && property.companyOwners.length > 0) {
      property.companyOwners.forEach(item => {
        this.makeDecimal(item, "ownershipPercentage");
      });
    }
    if (!!property.individualOwners && property.individualOwners.length > 0) {
      property.individualOwners.forEach(item => {
        this.makeDecimal(item, "ownershipPercentage");
      });
    }
    return property;
  };

  onSubmit = property => {
    const { sectionName, dealId } = this.props.match.params;
    if (this.validateProperty(property)) {
      if (sectionName === PROPERTY_SECTION_KEYS.NEW_PROPERTY) {
        this.props.propertyDetailCreateProperty({ dealId, property, callback: this.redirectToDetails });
      } else {
        const { dealId } = property;
        this.deleteFalsyValues(property);
        const clonedValues = cloneDeep(property);
        const propertyToSave = this.modifyValuesForSerialization(clonedValues);
        this.props.updateProperty({
          dealId,
          ...propertyToSave,
          callback: () => {
            this.setState({ isSaving: false });
            this.props.propertyDetailGetProperty({ propertyId: property.id });
          }
        });
      }
    }
  };

  startSave = () => this.setState({ isSaving: true });
  startCancel = () => this.setState({ isCanceling: true });
  finishSave = () => this.setState({ isSaving: false, isCanceling: false });

  renderBody = () => {
    const { sectionName } = this.props.match.params;
    const { propertyData, salesBrokerContact, propertyProjections } = this.props;
    const { property, ancillary } = propertyData;
    if (!propertyData || !ancillary) return null;
    const pp = propertyProjections ? propertyProjections.property : null;
    const data = { ...pp, ...property, accountingIntegrations: this.props.accountingIntegrations };

    let sectionRows = null;
    let configItems = {
      assetClass: sortBy(ancillary.assetClass, "name"),
      assetType: sortBy(ancillary.assetType, "name"),
      zoning: sortBy(ancillary.zoning, "name"),
      msa: sortBy(ancillary.msa, "name"),
      states: sortBy(ancillary.states, "name"),
      region: sortBy(ancillary.region, "name"),
      marketValueMethodology: sortBy(ancillary.marketValueMethodology, "name"),
      individualOwners: sortBy(ancillary.companyContacts, "name"),
      companyOwners: sortBy(ancillary.companies, "name"),
      salesBrokerCompany: sortBy(ancillary.salesBrokerCompany, "name"),
      propertyStatus: sortBy(ancillary.propertyStatus, "sortField"),
      propertyUse: sortBy(ancillary.propertyUse, "sortField"),
    };

    switch (sectionName) {
      case PROPERTY_SECTION_KEYS.PROPERTY_FINANCIALS:
        sectionRows = financialRows;
        break;
      case PROPERTY_SECTION_KEYS.PROPERTY_DETAILS:
        sectionRows = deatailRows;
        break;
      case PROPERTY_SECTION_KEYS.LOCATION:
        sectionRows = locationRows;
        break;
      case PROPERTY_SECTION_KEYS.PROPERTY_HISTORY:
        sectionRows = historyRows;
        break;
      case PROPERTY_SECTION_KEYS.NEW_PROPERTY:
        sectionRows = newPropertyRows;
        break;
      case PROPERTY_SECTION_KEYS.INDIVIDUAL_OWNERS:
        sectionRows = individualOwnerRows(this.props.isLoading);
        break;
      case PROPERTY_SECTION_KEYS.COMPANY_OWNERS:
        sectionRows = companyOwnerRows(this.props.isLoading);
        break;
      case PROPERTY_SECTION_KEYS.PROPERTY_DISPOSITION:
        sectionRows = dispositionRows;
        sectionRows.salesBrokerCompany.cells[0].onChange = id => this.props.getContactsByCompany({ companyId: id, subKey: "salesBrokerContact" });
        let salesBrokerContactAlt = [];
        if (data.salesBrokerCompanyId) {
          const salesBrokerCompany = ancillary.salesBrokerCompany.filter(x => x.id === data.salesBrokerCompanyId);
          salesBrokerContactAlt = salesBrokerCompany[0].contacts;
        }

        configItems = { ...configItems, salesBrokerContact: salesBrokerContact || salesBrokerContactAlt };
        break;
      case PROPERTY_SECTION_KEYS.PROJECTION_VARIABLES:
        sectionRows = projectionsRows;
        const date =
          propertyProjections &&
            propertyProjections.property &&
            propertyProjections.property.propertyProjections &&
            propertyProjections.property.propertyProjections[0] &&
            propertyProjections.property.propertyProjections[0].asOfDate
            ? propertyProjections.property.propertyProjections[0].asOfDate
            : "";
        const externalLabel = date ? `Projections updated on ${moment(date).format("MM/DD/YYYY")}` : "No Projections Available";
        sectionRows.update.cells[0].externalLabel = externalLabel;
        sectionRows.fileUpload.cells[0].value = externalLabel;
        sectionRows.fileUpload.cells[2].onDrop = (files, formik) => {
          const capRateAssumption = property.noiActual && property.marketValue ? property.noiActual / property.marketValue : null;
          const capRateOfProperty = this.getCapRateOfProperty(property);

          const propertyData = {
            periodType: formik.values.periodType,
            termInPeriod: formik.values.termInPeriod,
            asOfDate: formik.values.asOfDate,
            propertyId: property.id,
            capRateAssumption: capRateAssumption || null,
            occupancyAssumption: property.occupancy ? property.occupancy / 100 : null,
            dscrLenderAssumption: null,
            debtYieldAssumption: null,
            capRatecurrentValue: capRateOfProperty ? (parseFloat(capRateOfProperty) * 100).toFixed(2) : null,
            occupancycurrentValue: property.occupancy,
            noiActualcurrentValue: property.noiActual,
            noiLendercurrentValue: property.noi,
            dscrLendercurrentValue: property.dscrLender,
            debtYieldcurrentValue: property.debtYield,
            monthOffset: this.getMonthOffsetToStateForQuarter(formik.values.asOfDate),
            isPipeline: false,
            targetedClosing: this.props.targetedClosing
          };
          this.props.extractProjections({ file: files[0], propertyData });
        };
        sectionRows.download.cells[0].onClick = (_, formik) => {
          const { asOfDate } = formik.values;
          const { periodType } = formik.values;
          const { termInPeriod } = formik.values;
          if (!asOfDate || !periodType || !termInPeriod) ToastError("Please choose an As of Date, Period Type and Term before downloading the template.");
          else {
            const propertyData = {
              propertyId: property.id,
              edit: true,
              asOfDate: formik.values.asOfDate,
              periodType: formik.values.periodType,
              term: formik.values.termInPeriod
            };
            this.props.downloadProjections(propertyData);
          }
        };
        break;
      default:
        sectionRows = null;
        break;
    }

    if (!sectionRows) return null;

    return (
      <AccordionEditSection
        rows={sectionRows}
        configItems={configItems}
        finishSave={this.finishSave}
        isSaving={this.state.isSaving}
        isCanceling={this.state.isCanceling}
        data={data}
        onSubmit={this.onSubmit}
        onCancel={this.finishSave}
        eventHandlers={{ onAddIntegration: this.handleOnAddIntegration, onRemoveIntegration: this.handleOnRemoveIntegration }}
      />
    );
  };

  handleOnAddIntegration = fieldName => {
    if (!this.props.deal) {
      this.props.dealGet({ dealId: this.props.propertyData.property.dealId });
    }
    this.setState({ isLenderReportingDrawerOpen: true, fieldNameForLenderReporting: fieldName });
  };

  handleOnRemoveIntegration = accountingIntegration => {
    this.setState({ removeAccountingIntegrationConfirmationModalOpen: true, accountingIntegrationToRemove: accountingIntegration });
  };

  removeIntegration = accountingIntegration => {
    this.props.deleteAccountingIntegration(accountingIntegration.id);
    const pathParts = accountingIntegration.accountingIntegrationField.path.split(".");
    if (pathParts.length !== 2) {
      throw new Error(`Expected exactly 2 Lender Reporting path parts but found ${pathParts.length}.  This is a bug in the code or form rows definition file.`);
    }
    this.props.propertyData.property[pathParts[1]] = null;
    this.setState({ removeAccountingIntegrationConfirmationModalOpen: false, accountingIntegrationToRemove: null });
  };

  createAccountingIntegration = field => {
    this.props.createAccountingIntegration(
      this.state.fieldNameForLenderReporting,
      this.props.match.params.propertyId,
      field.id,
      field.sourceFileId,
      this.props.propertyData.property.dealId
    );
    this.setState({ isLenderReportingDrawerOpen: false, fieldNameForLenderReporting: undefined });
    const fieldNameParts = this.state.fieldNameForLenderReporting.split(".");
    if (fieldNameParts.length !== 2) {
      throw new Error(
        `Expected exactly 2 Lender Reporting field name parts but found ${fieldNameParts.length}.  This is a bug in the code or form rows definition file.`
      );
    }
    if (fieldNameParts[1] === "occupancy") {
      this.props.propertyData.property[fieldNameParts[1]] = field.value * 100;
    } else {
      this.props.propertyData.property[fieldNameParts[1]] = field.value;
    }
  };

  closeLenderReportingDrawer = () => {
    this.setState({ isLenderReportingDrawerOpen: false });
  };

  getCapRateOfProperty = property =>
    !property.noiActual || !property.marketValue || property.marketValue == 0 ? null : (property.noiActual / property.marketValue).toFixed(4);

  getMonthOffsetToStateForQuarter = date => {
    const check = moment(date, "MM/DD/YYYY");
    if (!date || !check) return 0;
    const monthNum = check.format("M");
    return Quarters[monthNum] === 4 ? 3 : 0;
  };

  render() {
    const { propertyId, sectionName } = this.props.match.params;
    const backLabel = sectionName === PROPERTY_SECTION_KEYS.NEW_PROPERTY ? "Cancel" : "Property Details";
    const buttonListOverride =
      sectionName === PROPERTY_SECTION_KEYS.PROJECTION_VARIABLES
        ? [{ label: "Download Projections", onClick: () => this.props.downloadProjections({ propertyId }) }]
        : null;

    return (
      <>
        <SimpleInfoModal
          title="Remove integration"
          content="Removing this integration will delete all its underlying data. Are you sure you want to do this?"
          acceptLabel="Remove"
          isOpen={this.state.removeAccountingIntegrationConfirmationModalOpen}
          onClose={() => this.setState({ removeAccountingIntegrationConfirmationModalOpen: false })}
          onAccept={() => this.removeIntegration(this.state.accountingIntegrationToRemove)}
        />
        <LenderReportingDrawer
          authentication={this.props.authentication}
          closeDrawer={this.closeLenderReportingDrawer}
          deal={this.props.deal}
          initialDrawerSectionName="field-management"
          isOpen={this.state.isLenderReportingDrawerOpen && this.props.deal}
          isChoosingForAccountingIntegration
          onChooseForAccountingIntegration={this.createAccountingIntegration}
          propertyId={propertyId}
        />
        <CardPageLayout
          {...this.props}
          isReversed
          bannerImageUrl={this.props.propertyData?.property?.deal?.imageLarge}
          cardComponent={
            <PropertyHeroCard
              save={this.startSave}
              cancel={this.startCancel}
              buttonListOverride={buttonListOverride}
              {...{ ...this.props, hideButtons: false, backLabel }}
            />
          }
          bodyComponent={<div className="d-flex f-1 flex-column">{this.renderBody()}</div>}
        />
      </>
    );
  }
}

const mapStateToProps = ({ newPropertyDetail, authentication, configItems, newDeal, accountingIntegration }) => ({
  ...newPropertyDetail,
  company: mapCompanySelector(authentication),
  authentication,
  propertyConfigItems: configItems.configItemsByParents.propertyEdit || {},
  deal: newDeal.deal,
  accountingIntegrations: accountingIntegration.items,
  sourceFile: accountingIntegration.sourceFile
});

export default compose(
  withRouter,
  connect(mapStateToProps, {
    propertyDetailGetProperty,
    propertyDetailGetDealMetrics,
    propertyDetailGetNewProperty,
    propertyDetailCreateProperty,
    updateProperty,
    createProperty,
    deleteProperty,
    getConfigItemsByParents,
    getContactsByCompany,
    getCompaniesByTypeId,
    getSalesBrokers,
    getPropertiesAndProjectionsOfDealByPropertyId,
    downloadProjections,
    extractProjections,
    getAccountingIntegrationsForModel,
    createAccountingIntegration,
    deleteAccountingIntegration,
    dealGet
  })
)(PropertySectionEdit);
